import { Loader2, Trash2 } from "lucide-react";
import React from "react";

import {
  AlertDialog as ShadcnAlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
interface AlertDialogProps {
  triggerText?: string;
  title: string;
  description?: string;
  helpContent?: React.ReactNode;
  helpLink?: string;
  confirmText: string;
  cancelText?: string;
  onConfirm: () => void;
  onClose?: () => void;
  children?: React.ReactNode;
  trigger?: React.ReactNode;
  maxWidth?: string;
  triggerColor?: string;
  confirmColor?: string;
  cancelColor?: string;
  variant?: "default" | "destructive";
  size?: "1" | "2" | "3" | "4";
  align?: "start" | "center";
  loading?: boolean;
  contentClassName?: string;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  icon?: React.ReactNode;
}

const AlertDialog: React.FC<AlertDialogProps> = ({
  title,
  description,
  confirmText,
  cancelText,
  loading,
  onConfirm,
  onClose,
  children,
  trigger,
  maxWidth,
  confirmColor = "red",
  variant,
  contentClassName,
  disabled = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  icon,
  cancelText: customCancelText,
}) => {
  const isDestructive = variant === "destructive" || confirmColor === "red";

  const handleCancel = () => {
    onClose?.();
    controlledOnOpenChange?.(false);
  };

  return (
    <ShadcnAlertDialog
      open={controlledOpen}
      onOpenChange={controlledOnOpenChange}
    >
      {trigger && <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>}

      <AlertDialogContent
        style={maxWidth ? { maxWidth } : undefined}
        className={cn(
          "gap-0 overflow-hidden p-0",
          isDestructive ? "!bg-[var(--dialog-bg)] border-border dark:border-[#3A3A3A]" : "",
          contentClassName
        )}
      >
        {isDestructive ? (
          <>
            <AlertDialogHeader className="px-4 pt-4 pb-3 text-left">
              <AlertDialogTitle className="text-[15px] font-semibold text-foreground">
                {title}
              </AlertDialogTitle>
              {description && (
                <AlertDialogDescription className="text-[13px] mt-1 text-muted-foreground leading-snug">
                  {description}
                </AlertDialogDescription>
              )}
              {children && (
                <div className={description ? "pt-2" : "pt-1"}>{children}</div>
              )}
            </AlertDialogHeader>

            <AlertDialogFooter className="px-4 pb-4 pt-0 sm:justify-end gap-2">
              <AlertDialogCancel
                onClick={handleCancel}
                disabled={loading}
                className="shadow-none m-0 border-none !bg-[var(--dialog-cancel-bg)] hover:!bg-[var(--dialog-cancel-hover)] !text-[var(--dialog-cancel-fg)] transition-colors h-8 text-[13px] px-3"
              >
                {customCancelText ?? cancelText ?? "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={disabled || loading}
                onClick={(e) => {
                  e.preventDefault();
                  onConfirm();
                }}
                className="shadow-none m-0 border-none !bg-[var(--dialog-delete-bg)] hover:!bg-[var(--dialog-delete-hover)] !text-[var(--dialog-delete-fg)] transition-colors h-8 text-[13px] px-3"
              >
                {loading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                {confirmText}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader className="flex-row items-start gap-3 space-y-0 px-5 py-4 text-left">
              <AlertDialogMedia>
                {icon ?? <Trash2 />}
              </AlertDialogMedia>
              <div className="min-w-0 flex-1 space-y-1">
                <AlertDialogTitle className="text-[15px] font-semibold">
                  {title}
                </AlertDialogTitle>
                {description && (
                  <AlertDialogDescription className="text-[13px]">
                    {description}
                  </AlertDialogDescription>
                )}
                {children && (
                  <div className={description ? "pt-1" : undefined}>{children}</div>
                )}
              </div>
            </AlertDialogHeader>

            <AlertDialogFooter className="border-t border-border bg-muted/40 px-5 py-3">
              <AlertDialogCancel
                onClick={handleCancel}
                disabled={loading}
                className="shadow-none"
              >
                {customCancelText ?? cancelText ?? "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction
                variant="default"
                disabled={disabled || loading}
                onClick={(e) => {
                  e.preventDefault();
                  onConfirm();
                }}
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {confirmText}
              </AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </ShadcnAlertDialog>
  );
};

export default AlertDialog;
